import React, { useEffect } from "react";
import cn from "classnames";
import Upload from "../Icons/Upload";
import CheckMark from "../Icons/CheckMark";
import Trash from "../Icons/Trash";

import styles from "./UploadField.module.scss";

export interface BaseFieldProps<T extends HTMLInputElement> extends Partial<React.HTMLProps<T>> {
    fileName: string;
    setFileName: (x: string) => void;
    fileStats: string;
    setFileStats: (x: string) => void;
    clear: () => void;
    fieldErrors?: string[];
    label?: string;
    details?: string;
    id?: string;
    className?: string;
    fileTypes?: string[];
    fileSizeMB?: number;
}

const dateFormatter = new Intl.DateTimeFormat('en-us', {
    year: "numeric",
    month: "numeric",
    day: "numeric"
});

/**
 * Generates a description string of which file types and max size a file can be.
 */
const getFileList = (fileTypes: string[], fileSize: number) => {
    const files = [...fileTypes];
    const last = files.pop();
    const fileList = `${files.join(", ")} or ${last}`;
    const firstChar = files?.[0]?.replace(/^\./, "");
    const article = /[aeio]/.test(firstChar?.charAt(0)) ? "an" : "a";
    const prefix = files.length ? `Use ${article}` : "";
    const size = fileSize ? `(${fileSize}MB)` : "";
    return prefix || size ? [prefix, fileList, size].join(" ") : "";
};

/**
 * Generates an upload file input field with various functionalities such as uploading, deleting,
 * and editing files,displaying file details, and handling errors.
 *
 * @returns {React.ReactElement} The Upload file input element.
 */
export function UploadField(props: BaseFieldProps<HTMLInputElement>): React.ReactElement {
    const {
        id,
        className,
        fieldErrors = [],
        required = false,
        fileTypes = [],
        fileSizeMB = 0,
        label,
        onChange = () => { },
        clear = () => {},
        fileName,
        setFileName,
        fileStats,
        setFileStats,
        ...restProps
    } = props;

    const hiddenFileInput = React.useRef<HTMLInputElement | null>(null);
    const today = React.useRef<string>("");

    useEffect(() => {
        today.current = dateFormatter.format(new Date()).toString();
    }, []);

    const handleUpload = () => {
        const hiddenInput = hiddenFileInput?.current;
        if (hiddenInput) {
            hiddenInput.value = "";
        }
        hiddenFileInput?.current?.click();
    };

    const handleDelete = () => {
        setFileName("");
        setFileStats("");
        fieldErrors.length = 0;
        const hiddenInput = hiddenFileInput?.current;
        setTimeout(() => {
            clear();
        });
        if (hiddenInput) {
            hiddenInput.value = "";
        }
    }

    const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFileName(event.target?.files?.[0].name ?? "");
        setFileStats(`Uploaded on ${today.current}`);
        setTimeout(() => {
            onChange(event);
        });
    };

    const btnClass = cn(styles.upload, fileName && styles.uploadSuccess);
    const compiledButtonClass = cn(btnClass, fieldErrors.length && styles.uploadError);
    const iconClass = fieldErrors.length ? cn(styles.uploadIcon, styles.uploadError) : styles.uploadIcon;

    const detailsText = !fieldErrors.length && fileName
        ? fileStats
        : getFileList(fileTypes, fileSizeMB)

    return (
        <div className={cn(styles.field, className)}>
            <div className={compiledButtonClass}>
                <div role="button" className={iconClass} onClick={handleUpload}>
                    {!fieldErrors.length && fileName
                        ? <CheckMark title="Upload Successful" />
                        : <Upload title={`${label} Button`} titleId={`${id}-upload`} />}
                </div>

                <div role="button" className={styles.uploadTextBlock} onClick={handleUpload}>
                    <p className={styles.uploadTitle}>{fileName ?? label}</p>
                    <p className={styles.uploadDetails}>{detailsText}</p>
                </div>

                {fileName ? (
                    <div className={styles.uploadEdit}>
                        <span role="button" onClick={handleUpload}>Edit</span>
                        <Trash role="button" title="Delete" onClick={handleDelete} />
                    </div>
                ) : null}
            </div>
            <input
                ref={hiddenFileInput}
                id={id}
                className={styles.hiddenInput}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                accept={fileTypes.join(",")}
                type="file"
                aria-required={required}
                aria-hidden="true"
                aria-label={label}
                required={required}
                onChange={onFileChange}
                defaultValue={""}
                tabIndex={-1}
                {...restProps}
            />
            <div className={styles.groupError} role="alert">
                {fieldErrors.map((error) => (
                    <p key={error}>
                        Error: {error}
                    </p>
                ))}
            </div>
        </div>
    );
}
